import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Stars } from "@/components/travel/Stars";
import { useAuth } from "@/context/AuthContext";
import { Users, Plane, Package, Euro, ShieldCheck, RotateCcw, FileText, Play } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import ActivityFeed from "@/components/travel/ActivityFeed";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [refundSid, setRefundSid] = useState("");
  const [simResult, setSimResult] = useState(null);

  const loadAll = async () => {
    try {
      const [s, u, a] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/audit?limit=50")]);
      setStats(s.data); setUsers(u.data); setLogs(a.data);
    } catch (err) { console.error("admin load failed", err); }
  };
  useEffect(() => { loadAll(); }, []);

  const refund = async () => {
    if (!refundSid) return;
    try { const r = await api.post("/admin/refund", { session_id: refundSid }); toast.success(`Refund ${r.data.refund_id}`); setRefundSid(""); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const resetSandbox = async () => {
    if (!confirm("Effacer toutes les données (sauf admins) ?")) return;
    try { await api.post("/admin/reset-sandbox"); toast.success("Sandbox reset"); setSimResult(null); loadAll(); }
    catch { toast.error("Erreur"); }
  };

  const simulateMission = async () => {
    try {
      const r = await api.post("/admin/simulate/full-mission", { action: "seed" });
      setSimResult(r.data);
      toast.success("Mission de démo créée — trajet + contact + chat + séquestre gelé");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const simulateAction = async (action, extra) => {
    try {
      await api.post("/admin/simulate/full-mission", { action, ...extra });
      toast.success(`Étape « ${action} » exécutée`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (!user?.is_admin) return <div className="p-8 text-center text-slate-400">Accès admin uniquement.</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl font-bold">Back-office Admin</h1>
        <div className="flex gap-2">
          <button data-testid="admin-simulate" onClick={simulateMission} className="btn-coral px-3 py-1.5 rounded-lg text-xs flex items-center gap-1"><Play size={12}/> Simuler mission complète</button>
          <button data-testid="admin-reset" onClick={resetSandbox} className="btn-ghost-glass px-3 py-1.5 rounded-lg text-xs flex items-center gap-1"><RotateCcw size={12}/> Reset sandbox</button>
        </div>
      </div>

      {simResult && (
        <div className="glass-strong rounded-xl p-4 mb-6 text-sm">
          <div className="font-semibold mb-2 text-emerald-400">✓ Mission simulée créée</div>
          <div className="grid md:grid-cols-2 gap-2 text-xs">
            <div>Voyageur : <code className="text-[#F59E0B]">{simResult.traveler_login?.email}</code> / <code>{simResult.traveler_login?.password}</code></div>
            <div>Buddy : <code className="text-[#F59E0B]">{simResult.buddy_login?.email}</code> / <code>{simResult.buddy_login?.password}</code></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link data-testid="sim-goto-messages" to={`/messages/${simResult.contact_id}`} className="btn-ghost-glass px-3 py-1.5 rounded-lg text-xs">Ouvrir le chat</Link>
            <button data-testid="sim-release" onClick={()=>simulateAction("release", { escrow_id: simResult.escrow_id })} className="btn-ghost-glass px-3 py-1.5 rounded-lg text-xs">Libérer séquestre</button>
            <button data-testid="sim-review" onClick={()=>simulateAction("review", { contact_id: simResult.contact_id })} className="btn-ghost-glass px-3 py-1.5 rounded-lg text-xs">Ajouter avis mutuels</button>
          </div>
        </div>
      )}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          <KPI Ic={Users} label="Utilisateurs" v={stats.users} />
          <KPI Ic={Plane} label="Trajets" v={stats.trips} />
          <KPI Ic={Package} label="Contacts débloqués" v={stats.unlocked_contacts} />
          <KPI Ic={Euro} label="Paiements réussis" v={stats.paid_transactions} />
          <KPI Ic={ShieldCheck} label="Séquestres" v={stats.escrows} />
          <KPI Ic={Euro} label="CA (EUR)" v={`${stats.revenue_eur}€`} />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div>
          <div className="glass rounded-xl p-4 mb-4">
            <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Euro size={14}/> Remboursement manuel</div>
            <div className="flex gap-2">
              <input data-testid="admin-refund-sid" value={refundSid} onChange={e=>setRefundSid(e.target.value)} placeholder="cs_test_..." className="flex-1 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-sm outline-none"/>
              <button data-testid="admin-refund-btn" onClick={refund} className="btn-coral px-4 py-2 rounded-lg text-sm">Rembourser</button>
            </div>
          </div>
        </div>
        <ActivityFeed />
      </div>

      <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><FileText size={16}/> Journal d'audit</h2>
      <div className="glass rounded-xl overflow-hidden mb-6 max-h-64 overflow-y-auto">
        <table className="w-full text-xs">
          <tbody>
            {logs.map(l => (
              <tr key={l.log_id} className="border-t border-slate-800">
                <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{new Date(l.at).toLocaleString("fr-FR")}</td>
                <td className="px-3 py-1.5"><span className="text-[#F59E0B]">{l.action}</span></td>
                <td className="px-3 py-1.5 text-slate-400 truncate">{JSON.stringify(l.meta)}</td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td className="px-3 py-4 text-slate-500 text-center">Aucun événement.</td></tr>}
          </tbody>
        </table>
      </div>

      <h2 className="font-display text-xl font-bold mb-3">Utilisateurs</h2>
      <div className="glass rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60"><tr>
            <th className="text-left px-3 py-2">Nom</th><th className="text-left px-3 py-2">Email</th>
            <th className="text-left px-3 py-2">Note</th><th className="text-left px-3 py-2">Rôle</th>
          </tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.user_id} className="border-t border-slate-800">
                <td className="px-3 py-2">{u.name}</td>
                <td className="px-3 py-2 text-slate-400">{u.email}</td>
                <td className="px-3 py-2"><Stars value={u.rating_avg} size={12}/> <span className="text-xs text-slate-500">({u.rating_count})</span></td>
                <td className="px-3 py-2">
                  {u.is_admin ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#FF6B6B]/10 text-[#FF6B6B]">admin</span> : <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">user</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPI({ Ic, label, v }) {
  return (
    <div className="glass rounded-xl p-4">
      <Ic size={16} className="text-[#F59E0B]"/>
      <div className="text-xs text-slate-400 mt-2">{label}</div>
      <div className="font-display text-xl font-bold">{v}</div>
    </div>
  );
}
